import { NavLink, useNavigate } from "react-router-dom";
import { useState } from "react";
import { usePais } from "@/context/PaisContext";
import {
  LayoutDashboard,
  Building2,
  FileText,
  ClipboardList,
  BellRing,
  Settings,
  Menu,
  X,
  Moon,
  Sun,
} from "lucide-react";
import logoPrevelis from "../assets/logo-prevelis.png";

const NAV_ES = [
  { to: "/", label: "Inicio", icon: LayoutDashboard, end: true },
  { to: "/empresas", label: "Empresas", icon: Building2 },
  { to: "/planes", label: "Planes de PRL", icon: FileText },
  { to: "/recordatorios", label: "Recordatorios", icon: BellRing },
  { to: "/formularios", label: "Formularios", icon: ClipboardList },
  { to: "/configuracion", label: "Configuración", icon: Settings },
];
const NAV_CH = [
  { to: "/ch", label: "Empresas", icon: Building2, end: true },
];

export function Layout({ children, title, subtitle, actions }) {
  const [open, setOpen] = useState(false);
  const [dark, setDark] = useState(() => document.documentElement.classList.contains("dark"));
  const navigate = useNavigate();
  const { pais, setPais } = usePais();
  const NAV = pais === "ch" ? NAV_CH : NAV_ES;
  const homePath = pais === "ch" ? "/ch" : "/";
  const cambiarPais = (p) => { setPais(p); navigate(p === "ch" ? "/ch" : "/"); setOpen(false); };

  const toggleTheme = () => {
    const el = document.documentElement;
    const next = !el.classList.contains("dark");
    el.classList.toggle("dark", next);
    try { localStorage.setItem("theme", next ? "dark" : "light"); } catch (e) { /* ignore */ }
    setDark(next);
  };

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      <button
        onClick={() => { navigate(homePath); setOpen(false); }}
        data-testid="brand-home"
        className="flex items-center px-6 h-20 border-b border-slate-200 text-left"
      >
        <img
          src={logoPrevelis}
          alt="PREVELIS"
          className="h-9 w-auto"
          data-testid="brand-logo"
        />
        <span className="ml-2 text-lg" title={pais === "ch" ? "Suiza" : "España"}>{pais === "ch" ? "🇨🇭" : "🇪🇸"}</span>
      </button>

      <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1">
        {NAV.map(({ to, label, icon: Icon, end }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            onClick={() => setOpen(false)}
            data-testid={`nav-${to === "/" ? "dashboard" : to.slice(1)}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 h-12 rounded-lg text-sm font-medium transition-colors ${
                isActive
                  ? "bg-blue-50 text-blue-800"
                  : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
              }`
            }
          >
            <Icon className="w-5 h-5 shrink-0" strokeWidth={1.75} />
            {label}
          </NavLink>
        ))}
      </nav>

      <div className="px-6 py-4 border-t border-slate-200 text-xs text-slate-400">
        PREVELIS · Asesoramiento técnico en PRL
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex">
      {/* Sidebar desktop */}
      <aside className="hidden lg:flex w-[280px] shrink-0 bg-white border-r border-slate-200 flex-col fixed inset-y-0 left-0">
        <SidebarContent />
      </aside>

      {/* Sidebar móvil */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-40 flex">
          <div className="absolute inset-0 bg-slate-900/40" onClick={() => setOpen(false)} />
          <aside className="relative w-[280px] bg-white border-r border-slate-200 flex flex-col">
            <SidebarContent />
          </aside>
        </div>
      )}

      {/* Main */}
      <div className="flex-1 lg:ml-[280px] flex flex-col min-w-0">
        <header className="sticky top-0 z-30 bg-white/90 backdrop-blur-md border-b border-slate-200 h-20 flex items-center gap-4 px-4 sm:px-6 lg:px-8">
          <button
            className="lg:hidden w-11 h-11 flex items-center justify-center rounded-lg hover:bg-slate-100"
            onClick={() => setOpen(!open)}
            data-testid="mobile-menu-toggle"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
          <div className="min-w-0 flex-1">
            <h1 className="font-heading font-bold text-xl sm:text-2xl tracking-tight text-slate-900 truncate">
              {title}
            </h1>
            {subtitle && <p className="text-sm text-slate-500 truncate">{subtitle}</p>}
          </div>
          <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1 shrink-0" data-testid="pais-switch">
            <button onClick={() => cambiarPais("es")} data-testid="switch-es"
              className={`px-2.5 h-9 rounded-md text-sm font-medium transition-colors ${pais !== "ch" ? "bg-white shadow text-slate-900" : "text-slate-500 hover:text-slate-800"}`}
              title="España">🇪🇸</button>
            <button onClick={() => cambiarPais("ch")} data-testid="switch-ch"
              className={`px-2.5 h-9 rounded-md text-sm font-medium transition-colors ${pais === "ch" ? "bg-white shadow text-slate-900" : "text-slate-500 hover:text-slate-800"}`}
              title="Suiza">🇨🇭</button>
          </div>
          <button
            onClick={toggleTheme}
            data-testid="theme-toggle"
            title={dark ? "Modo claro" : "Modo oscuro"}
            className="w-11 h-11 flex items-center justify-center rounded-lg hover:bg-slate-100 text-slate-600 shrink-0"
          >
            {dark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
          </button>
          {actions}
        </header>

        <main className="flex-1 p-4 sm:p-6 lg:p-8">{children}</main>
      </div>
    </div>
  );
}
